import { useState } from "react";
import { pct } from "../lib/hours";
import { dayLabel, hrs } from "../lib/time";
import { ClientChip } from "./bits";

const TABS = [["hours", "Client hours"], ["clients", "Clients"], ["people", "People"], ["design", "Design x client"], ["video", "Video x client"], ["log", "Time log"]];
const h2 = hrs;
const h1 = hrs;
const TYPE = { retainer: "Retainer", project: "Project", internal: "Internal" };

function Table({ head, rows, foot, testid }) {
  return (
    <div className="xt-wrap" role="region" tabIndex={0} aria-label={testid}>
      <table className="xt" data-testid={testid}>
        <thead><tr>{head.map((x, i) => <th key={i} scope="col" className={i ? "r" : ""}>{x}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{r.map((x, j) => j === 0 ? <th key={j} scope="row">{x}</th> : <td key={j} className="r num">{x}</td>)}</tr>)}</tbody>
        {foot && <tfoot><tr>{foot.map((x, i) => i === 0 ? <th key={i} scope="row">{x}</th> : <td key={i} className="r num">{x}</td>)}</tr></tfoot>}
      </table>
    </div>
  );
}

function Stats({ pv }) {
  const t = pv.totals;
  const S = ({ v, l, id }) => <span className="xs"><b className="num" data-testid={id}>{v}</b> <span className="lbl">{l}</span></span>;
  return (
    <div className="xstats2" data-testid="export-stats">
      <S v={h1(t.hours)} l="hours" id="export-total" /><S v={h1(t.designHours)} l="design hours" /><S v={h1(t.videoHours)} l="video hours" />
      <S v={h1(t.revisionHours)} l="revision hours" /><S v={t.people} l="people used" /><S v={t.clients} l="clients" />
    </div>
  );
}

function Matrix({ pv, team }) {
  const codes = pv.clients.filter((c) => pv.teamMatrix[team].some((p) => p.byClient[c.code])).map((c) => c.code);
  if (!pv.teamMatrix[team].length) return <p className="hint0">Nobody in {team === "design" ? "Design" : "Video"} logged time in this slice.</p>;
  return <Table testid={`xt-matrix-${team}`} head={["Person", ...codes, "Total"]}
    rows={pv.teamMatrix[team].map((p) => [p.name, ...codes.map((c) => (p.byClient[c] ? h1(p.byClient[c]) : "")), h1(p.total)])} />;
}

function Hours({ pv }) {
  const max = Math.max(0.0001, ...pv.clients.map((c) => c.hours));
  return (
    <div className="xt-wrap" role="region" tabIndex={0} aria-label="Client hours">
      <table className="xt xt-hours" data-testid="xt-hours">
        <thead><tr><th scope="col">Client</th><th scope="col">Type</th><th scope="col" className="r">Design hours</th><th scope="col" className="r">Video hours</th><th scope="col" className="r">Total hours</th></tr></thead>
        <tbody>{pv.clients.map((c) => (
          <tr key={c.code}><th scope="row"><ClientChip code={c.code} name={c.client} label /></th><td>{TYPE[c.type]}</td><td className="r num">{h1(c.designHours)}</td><td className="r num">{h1(c.videoHours)}</td>
            <td className="r num"><span className="xbar"><i style={{ width: `${(c.hours / max) * 100}%` }} /></span><b>{h1(c.hours)}</b></td></tr>))}</tbody>
        <tfoot><tr><th scope="row">Total</th><td /><td className="r num">{h1(pv.totals.designHours)}</td><td className="r num">{h1(pv.totals.videoHours)}</td><td className="r num"><b>{h1(pv.totals.hours)}</b></td></tr></tfoot>
      </table>
    </div>
  );
}

export function Preview({ pv }) {
  const [tab, setTab] = useState("hours");
  const count = { hours: pv.clients.length, clients: pv.clients.length, people: pv.people.length, log: pv.logCount,
    design: `${pv.teamMatrix.design.length}x${pv.clients.length}`, video: `${pv.teamMatrix.video.length}x${pv.clients.length}` };
  const body = {
    hours: <Hours pv={pv} />,
    clients: <Table testid="xt-clients" head={["Client", "Type", "Serviced by", "Hours", "Design", "Video", "Revision", "People"]}
      rows={pv.clients.map((c) => [c.client, c.type, c.owner || "", h2(c.hours), h2(c.designHours), h2(c.videoHours), `${h2(c.revisionHours)} (${pct(c.revisionPct)})`, c.people])}
      foot={["Total", "", "", h2(pv.totals.hours), h2(pv.totals.designHours), h2(pv.totals.videoHours), h2(pv.totals.revisionHours), pv.totals.people]} />,
    people: <Table testid="xt-people" head={["Person", "Team", "Hours", "Clients", "Top client", "Days", "Avg a day"]}
      rows={pv.people.map((p) => [p.person, p.team === "design" ? "Design" : p.team === "video" ? "Video" : p.team, h2(p.hours), p.clients, p.topClient, p.days, h2(p.avgPerDay)])} />,
    design: <Matrix pv={pv} team="design" />,
    video: <Matrix pv={pv} team="video" />,
    log: <>
      <Table testid="xt-log" head={["Date", "Person", "Client", "Card", "Kind", "Start", "End", "Minutes"]}
        rows={pv.log.slice(0, 50).map((l) => [dayLabel(l.date), l.person, l.client, l.card, l.kind, l.start, l.end, l.minutes])} />
      {pv.logCount > 50 && <p className="hint0">Showing the first 50 of {pv.logCount} timer stretches. The file has all of them.</p>}</>,
  };
  const onKey = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    const n = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: TABS.length - 1 }[e.key];
    if (n == null) return;
    e.preventDefault();
    const k = TABS[(n + TABS.length) % TABS.length][0];
    setTab(k);
    document.getElementById(`xtab-${k}`)?.focus();
  };
  return (
    <>
      <Stats pv={pv} />
      <div className="xtabs" role="tablist" aria-label="Preview sheets" onKeyDown={onKey}>
        {TABS.map(([k, l]) => <button key={k} id={`xtab-${k}`} role="tab" aria-selected={tab === k} tabIndex={tab === k ? 0 : -1} aria-controls="xpanel"
          className={tab === k ? "on" : ""} onClick={() => setTab(k)} data-testid={`export-tab-${k}`}>{l} <small className="num">{count[k]}</small></button>)}
      </div>
      <div id="xpanel" role="tabpanel" aria-labelledby={`xtab-${tab}`} className="xpanel" data-testid={`export-panel-${tab}`}>{body[tab]}</div>
    </>
  );
}
